import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONFIG } from '../config/variables';
import { useCMSData } from '../hooks/useCMSData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiChevronDown } = FiIcons;

const LocalCoverage = () => {
  const { seoLocations } = useCMSData();
  
  // If CMS provides SEO locations, render the enhanced section
  if (seoLocations && seoLocations.length > 0) {
    return <SEOLocations seoLocations={seoLocations} />;
  }

  // Fallback to original hardcoded locations
  const selectedLocations = CONFIG.SERVICE_LOCATIONS_EXTENDED.slice(0, 6);

  return (
    <section className="py-16 bg-gradient-to-br from-blue-900/10 to-purple-900/10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-white/80 mb-6">
            We build premium landing pages for trade businesses across {CONFIG.PRIMARY_LOCATION}, including areas like:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {selectedLocations.map((location, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                {location}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Merged SEOLocations component (previously in src/components/SEOLocations.jsx)
const SEOLocations = ({ seoLocations }) => {
  if (!seoLocations || seoLocations.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-br from-black to-gray-900 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-4 py-2 rounded-full mb-6">
            <SafeIcon icon={FiMapPin} className="text-blue-400" />
            <span className="text-blue-200 text-xs font-bold uppercase tracking-widest">Service Areas</span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Serving Trade Businesses Across Australia
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Premium landing pages crafted for local service providers in these regions
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {seoLocations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="text-blue-400 w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{location.displayGroup || location.Region || location.region}</h3>
                  <p className="text-white/40 text-xs uppercase font-bold tracking-widest">{location.state || location.State}</p>
                </div>
              </div>

              {location.suburbsCSV && (
                <div className="mb-4">
                  <p className="text-white/60 text-sm mb-2">Areas we serve:</p>
                  <div className="flex flex-wrap gap-2">
                    {parseSuburbs(location.suburbsCSV).slice(0, 6).map((suburb, i) => (
                      <span key={i} className="text-white/40 text-xs bg-white/5 px-2 py-1 rounded">
                        {suburb}
                      </span>
                    ))}
                    {parseSuburbs(location.suburbsCSV).length > 6 && (
                      <span className="text-blue-400 text-xs bg-blue-500/10 px-2 py-1 rounded">
                        +{parseSuburbs(location.suburbsCSV).length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(location.introCopy || location.BPAS_IntroCopy || location.aeoQuestion || location.BPAS_AEO_Question) && (
                <details className="group mt-4">
                  <summary className="flex items-center justify-between text-white/60 text-sm cursor-pointer hover:text-white transition-colors">
                    <span>More about {location.displayGroup || location.Region || location.region}</span>
                    <SafeIcon icon={FiChevronDown} className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    {(location.introCopy || location.BPAS_IntroCopy) && (
                      <p className="text-white/50 text-sm leading-relaxed mb-3">
                        {location.introCopy || location.BPAS_IntroCopy}
                      </p>
                    )}
                    {(location.aeoQuestion || location.BPAS_AEO_Question) && (location.aeoAnswer || location.BPAS_AEO_Answer) && (
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-blue-400 text-xs font-bold mb-1">
                          {location.aeoQuestion || location.BPAS_AEO_Question}
                        </p>
                        <p className="text-white/40 text-xs leading-relaxed">
                          {location.aeoAnswer || location.BPAS_AEO_Answer}
                        </p>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Parse comma-separated suburbs string into array
const parseSuburbs = (csv) => {
  if (!csv) return [];
  return csv.split(',').map(s => s.trim()).filter(s => s.length > 0);
};

export default LocalCoverage;